import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Video, Users, Clock, Shield, Mic, Camera, ScreenShare, MessageSquare, Play, ArrowRight, BookOpen, Lightbulb, Heart, Target, HelpCircle, Coffee, Zap, Volume2 } from "lucide-react";
import { Link } from "react-router-dom";

const ZoomStudyRoom = () => {
  const features = [
    {
      icon: <Video className="w-8 h-8" />,
      title: "HD Video",
      description: "Crisp video quality helps you see your study partners clearly, reinforcing the sense of being together",
    },
    {
      icon: <ScreenShare className="w-8 h-8" />,
      title: "Screen Share",
      description: "Collaborate on problem sets, annotate slides or review notes together with real-time sharing",
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Live Chat",
      description: "Text chat lets you ask quick questions without interrupting the flow of the session",
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Multiple Rooms",
      description: "Choose a room based on subject, size or vibe to create an optimal study space",
    },
  ];

  const faqData = [
    {
      question: "What's the difference between Zoom Study Rooms and regular Zoom calls?",
      answer: "Zoom Study Rooms are specifically designed for focused studying with features like synchronized Pomodoro timers, goal-sharing boards, and moderated environments. Unlike regular video calls, they create accountability through structured sessions and visual presence of other learners."
    },
    {
      question: "Do I need to have my camera on?",
      answer: "Camera usage is optional but recommended. Research shows that visual accountability significantly improves focus and motivation. You can choose rooms that match your comfort level – some are camera-optional while others encourage cameras for maximum accountability."
    },
    {
      question: "Are the study rooms moderated?",
      answer: "Yes, all rooms are actively moderated to maintain a respectful, productive environment. Report features ensure everyone can study comfortably, and disruptive behavior is promptly addressed."
    },
    {
      question: "What room type should I choose?",
      answer: "It depends on your task: Pomodoro Rooms are great for structured productivity with timed sessions, Silent Study is perfect for deep work with cameras on but mics muted, and Collaborative rooms are ideal for group projects and active discussion."
    },
    {
      question: "Can I use Zoom Study Rooms for group projects?",
      answer: "Absolutely! Collaborative rooms are specifically designed for group work with screen sharing, voice chat, and real-time document collaboration. You can brainstorm, solve problems together, and get immediate feedback from peers."
    },
  ];

  // JSON-LD structured data
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Zoom Study Rooms: Face-to-Face Focus for a Global Community",
    "description": "Video-enabled study rooms that bring the library experience to your screen. Join moderated sessions with HD video, screen sharing, and live chat for productive studying.",
    "url": "https://csw.live/zoom-study-room/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CSW.live",
      "url": "https://csw.live"
    },
    "about": {
      "@type": "Thing",
      "name": "Video Study Rooms"
    },
    "mentions": [
      { "@type": "Thing", "name": "social facilitation" },
      { "@type": "Thing", "name": "visual accountability" },
      { "@type": "Thing", "name": "virtual study groups" },
      { "@type": "Thing", "name": "Pomodoro technique" },
      { "@type": "Thing", "name": "collaborative learning" },
      { "@type": "Thing", "name": "peer support" },
      { "@type": "Thing", "name": "online study rooms" },
      { "@type": "Thing", "name": "body doubling" },
      { "@type": "Thing", "name": "screen sharing" },
      { "@type": "Thing", "name": "video conferencing for education" },
      { "@type": "Thing", "name": "synchronous learning" },
      { "@type": "Thing", "name": "study accountability" }
    ],
    "keywords": "zoom study room, video study room, virtual study group, online study session, study with webcam, face to face studying online, collaborative study, visual accountability, study accountability, Pomodoro study room, silent study room"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CSW Zoom Study Rooms",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "ratingCount": "2800"
    },
    "featureList": [
      "HD video study rooms",
      "Screen sharing collaboration",
      "Live text chat",
      "Pomodoro timer integration",
      "Silent study modes",
      "Collaborative discussion rooms",
      "Moderated safe environment",
      "Global study community"
    ],
    "description": "Free video-enabled study rooms with HD webcam sharing, screen share, and live chat. Study with partners in real-time with visual accountability."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://csw.live/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Zoom Study Room",
        "item": "https://csw.live/zoom-study-room/"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Zoom Study Rooms: Face-to-Face Focus for a Global Community"
        description="Video-enabled study rooms with HD webcam sharing, screen share, and live chat. Join moderated sessions with visual accountability and peer support. Free 24/7 access."
        keywords="zoom study room, video study room, virtual study group, online study session, study with webcam, face to face studying online, collaborative study, visual accountability, study accountability, Pomodoro study room"
        canonicalUrl="/zoom-study-room/"
      />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      
      <Header />
      <BlogReadingProgress />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-5xl mx-auto text-center space-y-8">
              {/* Breadcrumb */}
              <nav className="flex justify-center items-center gap-2 text-sm text-muted-foreground animate-fade-in">
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                <span>/</span>
                <span className="text-foreground">Zoom Study Room</span>
              </nav>
              
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
                <Video className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">VIDEO STUDY ROOMS</span>
              </div>
              
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <span className="text-gradient">Zoom Study Rooms:</span>{" "}
                <span className="text-foreground">Face‑to‑Face Focus for a Global Community</span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
                Studying alone can feel like running on a treadmill – it's easy to lose steam when there's no one around to keep pace. Video study rooms bring the library experience to your screen with{" "}
                <Link to="/study-with-me/" className="text-primary hover:underline">visual accountability</Link> and peer support.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Join a Zoom Room <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </Button>
              </div>
              
              {/* Video Section */}
              <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full aspect-video object-cover"
                  >
                    <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                      <Play className="w-5 h-5 text-primary-foreground ml-1" />
                    </div>
                    <span className="text-foreground font-medium">See how it works</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Video Study Rooms Work */}
        <section id="why-video-works" className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-6">
                Why Video Study <span className="text-gradient">Rooms Work</span>
              </h2>
              <p className="text-xl text-muted-foreground text-center mb-16 max-w-3xl mx-auto">
                Research-backed benefits of face-to-face virtual studying
              </p>
              
              <div className="space-y-12">
                {/* Visual Accountability */}
                <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl p-8">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                      <Camera className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-4">Visual Accountability and Social Facilitation</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                        Watching others work isn't just comforting – it actually improves your own performance. Psychologists call this{" "}
                        <strong className="text-foreground">social facilitation</strong>: people tend to do better when they know they're being observed.{" "}
                        <a 
                          href="https://muchacho.co.uk/why-online-study-rooms-improve-concentration/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Research on online study rooms
                        </a>{" "}
                        notes that seeing peers engaged in study encourages similar behaviour, enhancing motivation and sustaining focus.
                      </p>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        Visible timers and goal‑sharing boards reinforce this accountability.{" "}
                        <a 
                          href="https://www.academync.com/articles/study-groups-for-online-students-creating-connection-in-virtual-learning" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Another study found
                        </a>{" "}
                        that virtual study groups provide natural peer pressure to show up and stay engaged, turning procrastination into progress.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Real-time Connection */}
                <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl p-8">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                      <Users className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-4">Real‑Time Connection and Support</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                        Virtual study rooms replicate the camaraderie of an in‑person library. When students interact in real time – whether by video, voice or chat – they feel more connected and supported.{" "}
                        <a 
                          href="https://pmc.ncbi.nlm.nih.gov/articles/PMC8492824/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          A scoping review of virtual learning environments
                        </a>{" "}
                        concluded that high‑quality online experiences focus on communication between students and teachers and between peers, and that synchronous connections help meet learners' social and emotional needs.
                      </p>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        <a 
                          href="https://newlane.edu/virtual-study-groups-in-online-learning/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Educational research
                        </a>{" "}
                        noted that collaborative study groups offer shared insights, peer support and improved study skills, allowing students to better retain material through discussion and problem‑solving.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Community Reduces Isolation */}
                <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl p-8">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                      <Heart className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-4">Community Reduces Isolation</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        Online learning can be isolating, but joining a study room combats that loneliness.{" "}
                        <a 
                          href="https://elearning.adobe.com/2025/02/how-to-build-an-effective-virtual-study-group/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Articles on virtual study groups
                        </a>{" "}
                        emphasise that group sessions reduce feelings of isolation and foster camaraderie. Having study partners also provides built‑in exam prep: group members quiz one another, share resources and offer moral support. By tapping into a global community, you're reminded that you're not alone on your academic journey.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section id="features" className="py-24 relative overflow-hidden">
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                  Features That Power <span className="text-gradient">Productivity</span>
                </h2>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  Zoom study rooms aren't just a video call – they're equipped with tools designed for focused work
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {features.map((feature, index) => (
                  <Card 
                    key={index} 
                    className="text-center bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in"
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <CardHeader>
                      <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                        {feature.icon}
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">{feature.description}</CardDescription>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Room Types */}
        <section id="room-types" className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-6">
                Room Types for Every <span className="text-gradient">Workflow</span>
              </h2>
              <p className="text-xl text-muted-foreground text-center mb-16 max-w-3xl mx-auto">
                Different tasks call for different vibes. Choose the format that matches your needs.
              </p>
              <div className="grid md:grid-cols-3 gap-8">
                <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center text-orange-500 mb-4">
                    <Clock className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Pomodoro Rooms</h3>
                  <p className="text-muted-foreground mb-4">
                    These rooms synchronize everyone into 25–50 minute work blocks followed by short breaks. Structured study sessions align with our natural attention span and prevent burnout.
                  </p>
                  <Link to="/pomodoro-technique/" className="text-primary hover:underline text-sm font-medium">
                    Learn about Pomodoro →
                  </Link>
                </Card>

                <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center text-blue-500 mb-4">
                    <Volume2 className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Silent Study</h3>
                  <p className="text-muted-foreground mb-4">
                    For deep work, join a silent room. Cameras stay on for accountability but microphones are muted, letting you concentrate without chatter. Mirrors the quiet of a library while providing visual support.
                  </p>
                  <Link to="/study-with-me/" className="text-primary hover:underline text-sm font-medium">
                    Explore silent study →
                  </Link>
                </Card>

                <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center text-green-500 mb-4">
                    <MessageSquare className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Collaborative</h3>
                  <p className="text-muted-foreground mb-4">
                    Need to brainstorm or work through a tricky concept? Collaborative rooms let everyone talk, screen share and solve problems together. Explaining concepts to others cements understanding.
                  </p>
                  <Link to="/group-study/" className="text-primary hover:underline text-sm font-medium">
                    Join group study →
                  </Link>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Community and Group Study Experience */}
        <section id="community" className="py-24 relative overflow-hidden">
          <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-6">
                Community and <span className="text-gradient">Group Study</span> Experience
              </h2>
              <p className="text-xl text-muted-foreground text-center mb-16 max-w-3xl mx-auto">
                The social benefits that make video study rooms so effective
              </p>
              
              <div className="space-y-8">
                <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                  <div className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                      <Target className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold mb-2 text-foreground">Accountability and Motivation</h3>
                      <p className="text-muted-foreground text-lg">
                        Joining a video study room makes you accountable not just to yourself but to your peers. Educational articles stress that study groups provide natural accountability and motivation; knowing that others are counting on you helps you stick to your schedule and avoid procrastination. A virtual study group also offers a structured environment where participants set shared goals, schedule regular sessions and track progress.
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                  <div className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                      <Lightbulb className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold mb-2 text-foreground">Diverse Perspectives and Richer Learning</h3>
                      <p className="text-muted-foreground text-lg">
                        Group study isn't only about staying on task – it's about learning from each other. Diverse skill sets and perspectives expand collective knowledge. Explaining concepts to peers improves retention and deepens your understanding. And because study partners often come from different subjects or countries, you're exposed to new ideas and problem‑solving techniques.
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                  <div className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                      <Heart className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold mb-2 text-foreground">Reduced Isolation and Mental Health Benefits</h3>
                      <p className="text-muted-foreground text-lg">
                        <a 
                          href="https://pmc.ncbi.nlm.nih.gov/articles/PMC8492824/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Research into virtual learning environments
                        </a>{" "}
                        emphasizes that synchronous face‑to‑face connections support student well‑being and social‑emotional needs. Being part of a live study room combats loneliness and creates a sense of belonging. When you know you're studying alongside others who share your goals, it's easier to stay positive and engaged.
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-16">
                How It <span className="text-gradient">Works</span>
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { step: "1", title: "Choose a Room", desc: "Browse available rooms by subject, size or vibe. Whether you're studying calculus or creative writing, there's a space that fits your focus level." },
                  { step: "2", title: "Set Preferences", desc: "Decide whether to turn your camera on, mute your mic or use screen share. Setting clear preferences helps create a comfortable environment for everyone." },
                  { step: "3", title: "Start Studying", desc: "Once you're in, declare your goal, start the timer and dive in. Feel free to chat, share your screen or quietly focus depending on the room type." },
                ].map((item, index) => (
                  <div key={index} className="text-center space-y-4 animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                    <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center glow-primary">
                      <span className="text-3xl font-bold text-primary-foreground">{item.step}</span>
                    </div>
                    <h3 className="text-xl font-bold text-foreground">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section id="tips" className="py-24 relative overflow-hidden">
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-6">
                Tips for Getting the <span className="text-gradient">Most</span> from Zoom Study Rooms
              </h2>
              <p className="text-xl text-muted-foreground text-center mb-16 max-w-3xl mx-auto">
                Maximize your productivity with these research-backed strategies
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { icon: <Target className="w-6 h-6" />, title: "Set Clear Goals", desc: "Before each session, jot down what you want to accomplish. Clear objectives improve focus and reduce the likelihood of aimless study." },
                  { icon: <Clock className="w-6 h-6" />, title: "Use Timers and Breaks", desc: "Follow the Pomodoro rhythm or create your own schedule. Timed sessions align with natural attention cycles and prevent fatigue." },
                  { icon: <Users className="w-6 h-6" />, title: "Participate Consistently", desc: "Make study rooms part of your routine. Regular participation conditions your brain to associate the room with productivity." },
                  { icon: <MessageSquare className="w-6 h-6" />, title: "Communicate Respectfully", desc: "Open and honest communication keeps everyone on the same page. Respect others' time and perspectives to foster a supportive environment." },
                  { icon: <Zap className="w-6 h-6" />, title: "Mix Up Your Rooms", desc: "Try silent, Pomodoro and collaborative formats to see what works best. Switching room types can keep your study routine fresh and prevent burnout." },
                ].map((tip, index) => (
                  <Card key={index} className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                        {tip.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-1 text-foreground">{tip.title}</h3>
                        <p className="text-muted-foreground">{tip.desc}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                  <HelpCircle className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-primary">FAQ</span>
                </div>
                <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                  Frequently Asked <span className="text-gradient">Questions</span>
                </h2>
              </div>
              
              <div className="bg-gradient-to-br from-primary/10 via-secondary/5 to-primary/10 rounded-3xl p-8 md:p-12">
                <Accordion type="single" collapsible className="space-y-4">
                  {faqData.map((faq, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`item-${index}`}
                      className="bg-background/80 backdrop-blur-sm rounded-xl border border-border/50 px-6"
                    >
                      <AccordionTrigger className="text-left text-lg font-medium hover:no-underline">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Ready to Study <span className="text-gradient">Together?</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Video‑enabled study rooms combine the best of both worlds: the structure and accountability of a library with the flexibility and reach of online learning. Research and educational sources agree that collaborative study improves motivation, comprehension and retention.
              </p>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Enter a Zoom Room Now
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Related Pages */}
        <section className="py-24 relative overflow-hidden">
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                  <BookOpen className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-primary">EXPLORE MORE</span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold">
                  Related <span className="text-gradient">Resources</span>
                </h2>
              </div>
              
              <div className="bg-gradient-to-br from-primary/10 via-secondary/5 to-primary/10 rounded-3xl p-8 md:p-12">
                <div className="grid md:grid-cols-3 gap-6">
                  <Link 
                    to="/study-stream/" 
                    className="group bg-background/80 backdrop-blur-sm rounded-xl p-6 border border-border/50 hover:border-primary/50 transition-all"
                  >
                    <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mb-2">Study Stream</h3>
                    <p className="text-muted-foreground text-sm">24/7 virtual study rooms with focus sessions and community support</p>
                  </Link>
                  
                  <Link 
                    to="/study-timer/" 
                    className="group bg-background/80 backdrop-blur-sm rounded-xl p-6 border border-border/50 hover:border-primary/50 transition-all"
                  >
                    <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mb-2">Study Timer</h3>
                    <p className="text-muted-foreground text-sm">Pomodoro timers and focus tracking for structured productivity</p>
                  </Link>
                  
                  <Link 
                    to="/study-with-strangers/" 
                    className="group bg-background/80 backdrop-blur-sm rounded-xl p-6 border border-border/50 hover:border-primary/50 transition-all"
                  >
                    <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mb-2">Study With Strangers</h3>
                    <p className="text-muted-foreground text-sm">Join a global community of learners in moderated public rooms</p>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ZoomStudyRoom;
